import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Avatar,
  CircularProgress,
  Tooltip,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Store as StoreIcon,
  Visibility,
  VisibilityOff,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { authService } from '../services/api';

const roleLabel = (role) => {
  switch (role) {
    case 'OWNER': return 'Propriétaire';
    case 'MANAGER': return 'Responsable';
    default: return 'Opticien';
  }
};

const roleColor = (role) => {
  switch (role) {
    case 'OWNER': return '#5E564F';
    case 'MANAGER': return '#C5A46D';
    default: return '#64748B';
  }
};

const TeamMembers = () => {
  const currentUser = authService.getCurrentUserFromStorage();
  const isOwner = currentUser?.role === 'ADMIN' || currentUser?.teamRole === 'OWNER' || !currentUser?.accountId;

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);

  const loadMembers = useCallback(async () => {
    if (!isOwner) return;
    setLoading(true);
    setError('');
    try {
      const data = await authService.getTeamMembers();
      setMembers(data);
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.response?.data?.error;
      if (status === 403) {
        setError(msg || 'Abonnement PRO requis pour gérer les opticiens.');
      } else {
        setError('Impossible de charger la liste des opticiens.');
      }
    } finally {
      setLoading(false);
    }
  }, [isOwner]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleOpenDialog = () => {
    setForm({ name: '', email: '', password: '' });
    setFormError('');
    setShowPassword(false);
    setDialogOpen(true);
  };

  const handleCreate = async () => {
    const email = (form.email || '').trim().toLowerCase();
    if (!form.name.trim() || !email || !form.password) {
      setFormError('Tous les champs sont requis.');
      return;
    }
    if (members.some((m) => (m.email || '').toLowerCase() === email)) {
      setFormError('Un opticien avec cet email existe déjà.');
      return;
    }
    setFormLoading(true);
    setFormError('');
    try {
      await authService.createTeamMember({ name: form.name.trim(), email, password: form.password, teamRole: 'MEMBER' });
      setDialogOpen(false);
      setSuccess('Opticien créé avec succès. Il peut maintenant se connecter avec son email et mot de passe.');
      await loadMembers();
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.response?.data?.error;
      if (status === 403) {
        setFormError(msg || 'Abonnement PRO requis.');
      } else if (status === 400) {
        setFormError(msg || 'Données invalides.');
      } else {
        setFormError(msg || 'Erreur lors de la création.');
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleConfirmDelete = (member) => {
    setMemberToDelete(member);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!memberToDelete) return;
    setLoading(true);
    setError('');
    try {
      await authService.deleteTeamMember(memberToDelete.id);
      setDeleteDialogOpen(false);
      setMemberToDelete(null);
      setSuccess('Opticien supprimé.');
      await loadMembers();
    } catch {
      setError('Erreur lors de la suppression.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* En-tête */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Opticiens du magasin
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Créez un compte de connexion par opticien. Chacun se connecte avec son propre email et mot de passe et accède aux mêmes données du magasin.
          </Typography>
        </Box>
        {isOwner && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Actualiser">
              <IconButton onClick={loadMembers} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              sx={{
                background: 'linear-gradient(135deg, #1F2937 0%, #4B5563 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4B5563 0%, #1F2937 100%)' },
              }}
            >
              Ajouter un opticien
            </Button>
          </Box>
        )}
      </Box>

      {/* Alerte si non propriétaire */}
      {!isOwner && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Seul le propriétaire du compte peut gérer les opticiens du magasin.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Compte actuel */}
      <Card sx={{ mb: 3, border: '2px solid', borderColor: '#5E564F', borderRadius: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <StoreIcon sx={{ color: '#5E564F' }} />
            <Typography variant="subtitle1" fontWeight="bold" color="#5E564F">
              Compte connecté actuellement
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: '#5E564F', width: 48, height: 48 }}>
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography variant="body1" fontWeight="600">
                {currentUser?.name || 'Sans nom'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentUser?.email}
              </Typography>
            </Box>
            <Chip
              label={roleLabel(currentUser?.teamRole || 'OWNER')}
              size="small"
              sx={{ bgcolor: roleColor(currentUser?.teamRole || 'OWNER'), color: '#fff', fontWeight: 700, ml: 'auto' }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Liste des opticiens */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {members.length === 0 && !error && (
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <PersonIcon sx={{ fontSize: 48, color: '#A7AFB9', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Aucun opticien ajouté
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Ajoutez des opticiens pour qu'ils puissent se connecter avec leur propre session.
                  </Typography>
                  {isOwner && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenDialog}
                      sx={{
                        background: 'linear-gradient(135deg, #1F2937 0%, #4B5563 100%)',
                        '&:hover': { background: 'linear-gradient(135deg, #4B5563 0%, #1F2937 100%)' },
                      }}
                    >
                      Ajouter un opticien
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
          {members.map((member) => (
            <Grid item xs={12} sm={6} md={4} key={member.id}>
              <Card sx={{ borderRadius: 3, height: '100%', position: 'relative', overflow: 'visible' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: roleColor(member.teamRole), width: 44, height: 44 }}>
                      <PersonIcon />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body1" fontWeight="600" noWrap>
                        {member.name || 'Sans nom'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {member.email}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Chip
                      label={roleLabel(member.teamRole)}
                      size="small"
                      sx={{ bgcolor: roleColor(member.teamRole), color: '#fff', fontWeight: 600 }}
                    />
                    {member.teamRole !== 'OWNER' && isOwner && (
                      <Tooltip title="Supprimer cet opticien">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleConfirmDelete(member)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog ajout opticien */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon />
            Ajouter un opticien
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            L'opticien pourra se connecter sur la page de connexion avec son propre email et mot de passe et accédera aux données du magasin.
          </Typography>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nom de l'opticien"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                required
                autoFocus
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email de connexion"
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mot de passe"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((v) => !v)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={formLoading}>
            Annuler
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={formLoading}
            sx={{
              background: 'linear-gradient(135deg, #1F2937 0%, #4B5563 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #4B5563 0%, #1F2937 100%)' },
            }}
          >
            {formLoading ? <CircularProgress size={20} color="inherit" /> : 'Créer le compte'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmation suppression */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Supprimer l'opticien</DialogTitle>
        <DialogContent>
          <Typography>
            Confirmer la suppression du compte de{' '}
            <strong>{memberToDelete?.name || memberToDelete?.email}</strong> ?
            Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamMembers;
